import { useGetAllViewPrefilteredRowsQuery, useGetAllViewRowsQuery } from 'store/services/flow/rows';
import { useGetUsableViewQuery } from 'store/services/flow/views';
import { useGetViewColumnsQuery } from 'store/services/flow/viewColumns';
import { memo, useCallback, useMemo } from 'react';

import type { Option } from 'components/mui/common/Autocomplete';
import { Autocomplete } from 'components/mui/common/Autocomplete';
import type { TableCell } from 'components/common/Table/tableFunctions';
import type { TablesFormItem } from 'types';

import { getTableViewParams } from 'utils/agendaRecord/agendaRecord';
import { useFormContext, useWatch } from 'react-hook-form';
import { isArray } from 'utils/typeChecks';
import { useSelector } from 'react-redux';
import { selectDrawer, selectFilters } from 'store/entities/drawer/selectors';
import { useMapFilterValues } from 'utils/hooks/userAgendaTable';

interface Props {
  label: string;
  value: string | Option | Option[];
  onChange: (value: unknown) => void;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  formItemId: string;
  tablesDefinition?: TablesFormItem;
  options?: string[];
}

export const RecordsFilterAutocomplete = memo(
  ({ label, value, onChange, multiple, disabled, error, formItemId, tablesDefinition, options }: Props) => {
    const { control, watch } = useFormContext();

    const { viewId = '', agendaId = '' } = useSelector(selectDrawer());
    const filterValues = useSelector(selectFilters(`userAgenda-listing-${viewId}`));

    const mapFilterValues = useMapFilterValues({
      referencedFormItemsMap: new Map<string, string>(),
    });

    const appliedFilters = mapFilterValues(filterValues);

    const watchedValue = useWatch({
      control,
      name: (tablesDefinition?.params ?? []).map((param) => param.valueFormItemId as string),
    });

    const skipQuery = useMemo(() => {
      return !tablesDefinition?.viewId;
    }, [tablesDefinition?.viewId]);

    const { data: { viewColumns = [] } = {} } = useGetViewColumnsQuery(
      {
        viewId: tablesDefinition?.viewId ?? '',
      },
      { skip: skipQuery }
    );

    const { data: { viewParameters = [] } = {} } = useGetUsableViewQuery(
      {
        parentId: agendaId,
        viewId: viewId,
        viewParentType: 'Agenda',
      },
      { skip: !viewId }
    );

    const showOnlyExistingValues = useMemo(
      () => viewParameters.find((param) => param.parameterId === formItemId)?.showOnlyExistingValues ?? false,
      [formItemId, viewParameters]
    );

    const params = useMemo(() => {
      const tableViewParams = getTableViewParams(viewColumns);

      tableViewParams.forEach((viewColumn) => {
        const param = tablesDefinition?.params?.find((p) => p.columnId === viewColumn.valueColumnId);

        if (!param) return;

        viewColumn.valueSourceType = 'FormItem';
        viewColumn.valueDirect = param.valueFormItemId
          ? (() => {
              const items = Array.isArray(watch(param.valueFormItemId))
                ? watch(param.valueFormItemId)
                : [watch(param.valueFormItemId)].filter(Boolean);
              const filteredValues = isArray(items) ? (items as Option[]).filter((item) => item.label).map((item) => item.label) : items;
              return filteredValues.length > 0 ? filteredValues : null;
            })()
          : null;
      });

      return tableViewParams;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tablesDefinition?.params, viewColumns, watch, watchedValue]);

    const { data: allViewRows } = useGetAllViewRowsQuery(
      {
        body: {
          rowIds: [],
          valueColumns: params ?? [],
          viewFilteringType: 'Filter',
        },
        params: {
          orderBy: '',
          search: '',
          orderDesc: false,
          page: 1,
          pageSize: 10000,
          value: '',
          tableId: tablesDefinition?.tableId ?? '',
          viewId: tablesDefinition?.viewId ?? '',
        },
      },
      {
        skip: skipQuery || !viewColumns.find((column) => column.columnId === tablesDefinition?.columnIdToShow)?.viewColumnId,
      }
    );

    const { data: allViewPrefilteredRows = [] } = useGetAllViewPrefilteredRowsQuery(
      {
        params: {
          orderBy: '',
          search: '',
          orderDesc: false,
          page: 1,
          pageSize: 10000,
          value: '',
          tableId: tablesDefinition?.tableId ?? '',
          viewId: tablesDefinition?.viewId ?? '',
        },
        body: {
          rowPrefilterRequest: {
            agendaId,
            viewId,
            formItemId,
            recordsRequest: {
              recordIds: [],
              valueFormItems: [],
              params: {
                page: 1,
                pageSize: 10000,
                orderBy: null,
                orderDesc: null,
                search: null,
                formItemId: null,
                referencedFormItemId: null,
              },
              filters: appliedFilters ?? [],
              skipViewConditions: false,
              evaluateVisualAttributes: true,
            },
          },
          filterRequest: {
            tableId: tablesDefinition?.tableId ?? '',
            viewId: tablesDefinition?.viewId ?? '',
            valueColumns: params ?? [],
            filters: [],
          },
          viewFilteringType: 'Filter',
        },
      },
      {
        skip:
          skipQuery || !viewColumns.find((column) => column.columnId === tablesDefinition?.columnIdToShow)?.viewColumnId || !showOnlyExistingValues,
      }
    );

    const rows = useMemo(() => {
      if (!options?.length) {
        return ((showOnlyExistingValues ? allViewPrefilteredRows : allViewRows?.items) ?? [])
          .map((row) => {
            const matchedCell = row.cells.find((cell) => cell.columnId === tablesDefinition?.columnIdToShow);
            return matchedCell ? { ...matchedCell, rowId: row.rowId } : null;
          })
          .filter(Boolean);
      }

      return options.map((option) => ({
        text: { value: option },
        columnId: tablesDefinition?.columnIdToShow ?? '',
        rowId: option,
      }));
    }, [options, showOnlyExistingValues, allViewPrefilteredRows, allViewRows?.items, tablesDefinition?.columnIdToShow]);

    const getOptionLabel = useCallback(
      (option?: TableCell | null) =>
        String(option?.text?.value ?? option?.email?.value ?? option?.phoneNumber?.value ?? option?.number?.value ?? option?.reference?.value ?? ''),
      []
    );

    const formatOption = useCallback(
      (rowId: string) => {
        const option = rows?.find((opt) => opt?.rowId === rowId);
        return {
          label: option ? getOptionLabel(option) : '',
          value: rowId,
        };
      },
      [getOptionLabel, rows]
    );

    const formatValue = useCallback(
      (fieldValue: string | Option | Option[]) => {
        if (!fieldValue) return multiple ? [] : '';
        if (!multiple) return fieldValue;

        return Array.isArray(fieldValue)
          ? fieldValue.map((item) => (typeof item !== 'string' ? formatOption(item.value) : item))
          : [formatOption(fieldValue.toString())];
      },
      [formatOption, multiple]
    );

    return (
      <Autocomplete
        label={label}
        value={formatValue(value)}
        onChange={onChange}
        options={
          (rows ?? []).map((option) => ({
            label: getOptionLabel(option),
            value: option?.rowId ?? '',
          })) ?? []
        }
        multiple={multiple}
        error={error}
        disabled={disabled}
        background="white"
      />
    );
  }
);
